<template>
    <div>
        <div class="row pt-4">
            <div class="col-8 pr-3">
                <DirectRecordStage
                    ref="directStage"
                    class="mb-3"
                    :holistic="holistic"
                    :skipPositioningCheck="skipPositioningCheck"
                    @state-change="onStateChange"
                    @postprocessed="onPostProcessed"
                />
                <div class="d-flex align-items-center justify-content-between btn-box">
                    <Button v-if="!saveLandmarks && !countdown" type="cancel"
                            @cancel="cancel"></Button>
                    <Button v-if="!saveLandmarks && !firstRecordingDone && !isCheckingPosition && !countdown"
                            type="button"
                            :disabled="loading || !mediaPipeIsLoaded || !holisticPrewarmed || isCheckingPosition || countdown"
                            :loading="loading || !mediaPipeIsLoaded || !holisticPrewarmed || isCheckingPosition || countdown"
                            label="sign_feedback.step2.start_recording"
                            :icon="['fas', 'circle']"
                            @click="startRecording"></Button>
                    <div v-if="firstRecordingDone && !saveLandmarks && postProcessedLandmarks.length && !isCheckingPosition && !countdown" class="d-flex">
                        <Button type="button"
                                class="mr-3"
                                label="sign_feedback.step2.restart_recording"
                                :icon="['fas', 'repeat']"
                                :disabled="loading || !mediaPipeIsLoaded || !holisticPrewarmed || isCheckingPosition || countdown"
                                :loading="loading || !mediaPipeIsLoaded || !holisticPrewarmed || isCheckingPosition || countdown"
                                @click="startRecording"></Button>
                        <Button type="button"
                                label="sign_feedback.step2.send_recording"
                                :icon="['far', 'share']"
                                @click="sendRecording"></Button>
                    </div>
                </div>
            </div>

            <div class="col-4 w-100">
                <SearchResultCard class="w-100 mt-n1"
                                  :search-result="sign"
                                  :show-region="false"
                                  :show-detail="false">
                </SearchResultCard>
                <div class="row mb-3 mt-4">
                    <span class="col-10 " v-html="$t('sign_feedback.step2.explanation_exercise')"></span>
                    <span class="col-2 text-center text-primary">
                        <font-awesome-icon
                            size="xl"
                            :icon="['far', 'repeat']" />
                    </span>
                </div>
                <div class="row mb-3">
                    <span class="col-10 " v-html="$t('sign_feedback.step2.explanation_light')"></span>
                    <span class="col-2 text-center text-primary">
                        <font-awesome-icon
                            size="xl"
                            :icon="['far', 'lightbulb']" />
                    </span>
                </div>
                <div class="row mb-3">
                    <span class="col-10 " v-html="$t('sign_feedback.step2.explanation_edge')"></span>
                    <span class="col-2 text-center text-primary">
                        <font-awesome-icon
                            size="xl"
                            :icon="['far', 'vector-square']" />
                    </span>
                </div>
                <div class="row">
                    <span class="col-10 " v-html="$t('sign_feedback.step2.explanation_slow')"></span>
                    <span class="col-2 text-center text-primary">
                        <font-awesome-icon
                            size="xl"
                            :icon="['far', 'turtle']" />
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import SearchResultCard from '@/components/search/SearchResultCard.vue';
import Button from '@/components/general/Button.vue';
import { ISignOverview } from '../../../../functions/src/shared/contracts/api/GlossContract';
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import DirectRecordStage from '@/components/signFeedback/DirectRecordStage.vue';

@Component({ components: { Button, SearchResultCard, DirectRecordStage } })
export default class RecordSign extends Vue {

    @Prop() sign: ISignOverview;
    @Prop() holistic: any;
    @Prop({ type: Boolean, default: false }) skipPositioningCheck!: boolean;

    @Emit('nextStep')
    public agreeAndNext() {
        return;
    }

    @Emit('cancel')
    public cancel() {
        return;
    }

    public loading = true;
    public mediaPipeIsLoaded = false;
    public holisticPrewarmed = false;

    public saveLandmarks = false;
    public firstRecordingDone = false;
    public countdown = false;
    public isPositioningValid = false;
    public positioningFeedback = '';
    public isCheckingPosition = false;

    public isPostProcessing = false;
    public postProcessingDone = false;
    public postProcessedLandmarks: any[] = [];
    public recordedVideoUrl: string | null = null;
    public recordedReplayReady = false;

    get recordedReplayPrepPercent(): number | null {
        if (!this.recordedReplayTotalCount || this.recordedReplayTotalCount <= 0) return null;
        const pct = Math.floor((this.recordedReplayProcessedCount / this.recordedReplayTotalCount) * 100);
        return Math.max(0, Math.min(100, pct));
    }

    public startRecording() {
        const stage: any = this.$refs['directStage'];
        if (stage && typeof stage.startRecording === 'function') {
            stage.startRecording();
        }
    }

    public sendRecording() {
        this.$emit('nextStep', this.postProcessedLandmarks);
    }
    public onPostProcessed(frames: any[]) {
        this.postProcessedLandmarks = frames || [];
    }
    
    public onStateChange(payload: any) {
        this.loading = !!payload.loading;
        this.mediaPipeIsLoaded = !!payload.mediaPipeIsLoaded;
        this.holisticPrewarmed = !!payload.holisticPrewarmed;
        this.saveLandmarks = !!payload.saveLandmarks;
        this.firstRecordingDone = !!payload.firstRecordingDone;
        this.countdown = !!payload.countdown;
        this.isCheckingPosition = !!payload.isCheckingPosition;
        this.isPostProcessing = !!payload.isPostProcessing;
        this.postProcessingDone = !!payload.postProcessingDone;
        this.recordedVideoUrl = payload.recordedVideoUrl || null;
        this.recordedReplayReady = !!payload.recordedReplayReady;
        // Optional counts from child for UI that may consume them
        this.recordedReplayProcessedCount = payload.recordedReplayProcessedCount || 0;
        this.recordedReplayTotalCount = payload.recordedReplayTotalCount || 0;
    }
    
    // Internal counters for recorded replay prep percent calculation (if needed by UI)
    private recordedReplayProcessedCount: number = 0;
    private recordedReplayTotalCount: number = 0;
}
</script>

<style lang="scss">
.box {
    position: relative;
}

.input_video,
.output_canvas {
    transform: rotateY(180deg);
}

.output_canvas {
    position: absolute;
    top: 0;
    left: 0;
}

.search-mock {
    position: absolute;
    top: 100px;
    left: 0;
    font-weight: 900;
    color: white;
}

.record-sign {
    position: absolute;
    top: 10px;
    right: 20px;
    border: 1px solid black;
    background-color: black;
    opacity: 0.7;
    border-radius: 5px;
    padding: 3px 5px;
    color: white;
    font-size: 12px;
}

.countdown {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
}

.countdown-timer, .countdown-timer-bar {
    height: 8px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 0.15rem;
}

.countdown-timer {
    margin-bottom: 2rem;
}

.countdown-timer-bar {
    background-color: $accent-color;
    transform-origin: left;
}

@keyframes countdown {
    to {
        transform: scaleX(0);
    }
}

.animate {
    animation: countdown 4s linear forwards;
}

.btn-box {
    height: 40px;
}

</style>

